/**
 * シフト編成（新）の API 契約と 1:1 の型（C3_PARTNER-358）
 * サーバ: com.ecometrue.attend.view.shiftBoard.*
 */

export type ViewType = 'day' | 'week' | 'half' | 'month' | 'custom';

/** 任意期間で表示できる日数の上限（シフト表の列数と読み込み量を抑える） */
export const CUSTOM_RANGE_MAX_DAYS = 62;
export type BreakdownAxis = 'none' | 'pattern' | 'business';

/** 業務フィルタで「業務が未設定のスタッフ」を表す予約値（サーバと合わせる） */
export const BUSINESS_ID_NONE = 'none';
export type LockReason =
  | 'ACHIEVEMENT_CONFIRMED'
  | 'ACHIEVEMENT_PROCESSING'
  | 'RECALCULATING'
  | 'SHIFT_CONFIRMED'
  | 'AWAY_SHIFT'
  | 'GUEST_STAFF'
  | 'WORK_SYSTEM'
  | 'SUPPORT_REJECTED';

export interface OrgInfo {
  orgCode: string;
  orgName: string;
  distinctionHoliday: boolean;
  closingHour: number;
  dispStartHour: number;
  dispHour: number;
  weekStartDow: number;
  monthStartDay: number;
  /** 勤怠の労務規約項目マスタの時間表記（0 = 10進、1 = 60進）。時間表記の既定値（9/16 決定） */
  timeDisplayFormat: number;
  /** 10進のときの小数第 3 位の丸め（0 = 切り捨て、1 = 切り上げ、2 = 四捨五入）。労務規約項目マスタ */
  roundMethodOfThirdDecimalPlace: number;
}

export interface PrintSetting {
  period: 'current' | 'half' | 'month' | 'custom';
  /** period = 'custom' のときの期間 */
  dateFrom: string | null;
  dateTo: string | null;
  paper: 'A4' | 'A3';
  /** ページの向き */
  orientation: 'landscape' | 'portrait';
  fontSize: 'large' | 'medium' | 'small';
  fitToWidth: boolean;
  items: Array<string>;
  monochrome: boolean;
  thickBorder: boolean;
  /** 枠線の色 */
  borderColor: string;
  /** 偶数行の背景色。null なら塗らない */
  stripeColor: string | null;
  /** 余白を取る辺 */
  marginTop: boolean;
  marginLeft: boolean;
  marginRight: boolean;
  /** 見出しの文字サイズ（%） */
  headerScale: number;
  /** スタッフ名列の文字サイズ（%） */
  staffNameScale: number;
  /** 業務ごとにページを分ける */
  splitByBusiness: boolean;
  header: boolean;
}

export interface BoardSetting {
  orgCode: string;
  defaultView: ViewType;
  slotMinutes: number;
  breakdownAxis: BreakdownAxis;
  staffSortType: number;
  submitEmploymentIds: Array<string>;
  printSetting: Partial<PrintSetting> | null;
}

export interface RoleInfo {
  editable: boolean;
  canConfirm: boolean;
  /** 正社員の人件費・人件費予算・交通費を表示してよいか（汎用項目2） */
  canViewLaborCostFulltime: boolean;
  /** アルバイトの人件費・人件費予算・交通費を表示してよいか（汎用項目3） */
  canViewLaborCostPart: boolean;
  canDownload: boolean;
  /** 雇用形態名を表示してよいか（権限マスタの汎用項目4） */
  canViewEmploymentName: boolean;
  /** 提出状況（シフト）のメニュー権限があるか */
  canViewSubmissions: boolean;
  /** 印刷してよいか（汎用項目5）。CSV 出力とは別（9/16 決定） */
  canPrint: boolean;
  /** 売上予算・実績を表示してよいか（汎用項目6）。予算と実績はセット */
  canViewSales: boolean;
  /**
   * 提出状況（シフト）を編集してよいか
   *
   * 9/16 に 4 権限へ分けたが 9/17 に編集ひとつへ戻した。
   * 編集が on なら回収枠を作る・締切を変更・締め切る・催促の 4 つとも行える。
   */
  canEditSubmissions: boolean;
}

/** 時間の表記（9/16 決定）。auto は勤怠の労務規約項目マスタに従う */
export type TimeFormat = 'auto' | 'hhmm' | 'decimal';

/** 共通設定（common_setting）のうちシフト編成が使うキー */
/**
 * 自分だけの表示設定（9/23 決定。人単位）
 *
 * 見やすさの好みなので、同じ店舗の他の人が変えても自分には影響しない。
 * 共通設定（common_setting）の staff 区分に保存する。
 * 値が無い項目は、店舗単位で保存されていた値（T1）→ 既定値の順に使う。
 */
export interface PersonalBoardSetting {
  /** 開いたときの表示単位 */
  defaultView: ViewType | null;
  /** 日ビューの刻み（分） */
  slotMinutes: number | null;
  /** 時間の表記 */
  timeFormat: TimeFormat;
  /** 印刷の設定（印刷画面で「この設定を覚える」を選んだとき） */
  printSetting: Partial<PrintSetting> | null;
}

export const DEFAULT_PERSONAL_SETTING: PersonalBoardSetting = {
  defaultView: null,
  slotMinutes: null,
  timeFormat: 'auto',
  printSetting: null,
};

/**
 * A20 簡易再計算の実行状況（9/18 に非同期化）
 *
 * 開始したときも、進み具合を見に来たときも同じ形が返る。
 */
export interface RecalculateResult {
  /** 実行の ID。これを添えて進み具合を見に行く */
  jobId: string | null;
  /** running / done / failed。まだ一度も実行していなければ空 */
  status: string | null;
  /** 対象にしたスタッフ数 */
  requested: number;
  /** 終わったスタッフ数（失敗を含む） */
  done: number;
  /** 再計算できたスタッフ数 */
  succeeded: number;
  /** 失敗したスタッフ数 */
  failedCount: number;
  failedStaffCodes: Array<string>;
  /** いま処理しているスタッフ */
  currentStaffName: string | null;
  /** 途中で落ちたときの理由 */
  message: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  /** 同じ組織で既に走っていたか */
  alreadyRunning: boolean;
  tooMany: boolean;
  maxStaff: number | null;
}

export interface PeriodInfo {
  periodId: string | null;
  name: string | null;
  dateFrom: string | null;
  dateTo: string | null;
  deadline: string | null;
  format: string | null;
  status: string | null;
  confirmStatus: 'NONE' | 'PARTIAL' | 'CONFIRMED';
  pendingDiffCount: number;
}

export interface DayHeader {
  date: string;
  dow: number;
  publicHolidayName: string | null;
  isStoreHoliday: boolean;
  shopPlan: string | null;
  confirmed: boolean;
  achievementConfirmed: boolean;
  achievementProcessing: boolean;
  locked: boolean;
  lockReason: LockReason | null;
  /** 祝日の前日（翌日が祝日）。予定人数の曜日 8 の判定に使う（10/5） */
  holidayEve?: boolean;
}

export interface StaffInfo {
  staffCode: string;
  staffName: string;
  initial: string;
  belongOrgCode: string;
  belongOrgName: string;
  isGuest: boolean;
  employmentId: string | null;
  employmentName: string | null;
  /** 役職名（期間の最終日に有効なもの）。名前の下に出す（9/24） */
  executiveName?: string | null;
  employmentType: 'FULLTIME' | 'PARTTIME' | null;
  mainBusinessId: string | null;
  mainBusinessName: string | null;
  businessIds: Array<string>;
  workSystem: number | null;
  closingHour: number;
  distinctionHoliday: boolean;
  hourlySalary: number | null;
  dailyTransportation: number | null;
  hasWeekPattern: boolean;
  hireDate: string | null;
  retirementDate: string | null;
  /** 名札写真のファイル名 */
  nameplate: string | null;
}

export interface ShiftDetailItem {
  shiftDetailId: string;
  start: string;
  end: string;
  isStartNextDay: boolean;
  isEndNextDay: boolean;
  businessId: string | null;
  businessName: string | null;
  isBreak: boolean;
  businessColor: string | null;
}

export interface SupportRequestInfo {
  status: number;
  deadline: string | null;
}

export interface ShiftItem {
  shiftId: string;
  staffCode: string;
  orgCode: string;
  belongOrgCode: string;
  targetDate: string;
  attendType: number;
  start: string | null;
  end: string | null;
  isEndNextDay: boolean;
  shiftPatternId: string | null;
  shiftPatternName: string | null;
  shiftPatternShortName: string | null;
  color: string | null;
  mainBusinessId: string | null;
  mainBusinessName: string | null;
  details: Array<ShiftDetailItem>;
  holidayId: string | null;
  holidayName: string | null;
  isLegal: boolean | null;
  breakMinutes: number;
  workMinutes: number;
  memo: string;
  supportRequest: SupportRequestInfo | null;
  isAway: boolean;
  awayOrgName: string | null;
  alerts: Array<string>;
  laborCost: number | null;
  transportation: number | null;
  locked: boolean;
  lockReason: LockReason | null;
  updateDate: string | null;
}

export interface HopeShiftItem {
  hopeShiftId: string;
  staffCode: string;
  targetDate: string;
  type: number;
  shiftPatternId: string | null;
  start: string | null;
  end: string | null;
  note: string | null;
}

export interface AchievementItem {
  achievementId: string;
  staffCode: string;
  targetDate: string;
  start: string | null;
  end: string | null;
  holidayName: string | null;
  isHolidayWork: boolean;
  transferType: number;
  hasPendingApplication: boolean;
}

export interface ViolationItem {
  type: string;
  staffCode: string;
  dates: Array<string>;
  message: string;
}

export interface DateSummary {
  date: string;
  headcount: number;
  plannedHeadcount: number | null;
  guestIn: number;
  lentOut: number;
  workMinutes: number;
  laborCostBudget: number | null;
  laborCost: number | null;
  laborCostFulltime: number | null;
  laborCostPart: number | null;
  transportation: number | null;
  salesBudget: number | null;
  salesActual: number | null;
  laborCostRate: number | null;
  budgetBasis: 'DAILY' | 'MONTHLY' | 'NONE';
}

export interface StaffSummary {
  staffCode: string;
  workDays: number;
  workMinutes: number;
  laborCost: number | null;
  transportation: number | null;
  publicHolidayCount: number;
  weeklyOver: boolean;
}

export interface TotalSummary {
  workMinutes: number;
  laborCost: number | null;
  transportation: number | null;
  salesBudget: number | null;
  laborCostBudget: number | null;
  laborCostRate: number | null;
  headcountOverDays: Array<string>;
  headcountUnderDays: Array<string>;
  laborCostOverDays: Array<string>;
}

export interface SummaryInfo {
  byDate: Array<DateSummary>;
  byStaff: Array<StaffSummary>;
  total: TotalSummary;
}

export interface PatternDetailInfo {
  start: string;
  end: string;
  isStartNextDay: boolean;
  isEndNextDay: boolean;
  businessId: string | null;
}

export interface PatternInfo {
  shiftPatternId: string;
  code: string;
  name: string;
  shortName: string;
  employmentId: string | null;
  start: string | null;
  end: string | null;
  color: string | null;
  details: Array<PatternDetailInfo>;
}

export interface BusinessInfo {
  businessId: string;
  businessCode: string;
  businessName: string;
  isBreak: boolean;
  color: string | null;
}

export interface OrgSimple {
  orgCode: string;
  orgName: string;
}

export interface BreakdownInfo {
  key: string | null;
  required: number;
  assigned: number;
}

export interface RequiredStaffInfo {
  date: string;
  /** DAILY = 特定日、TEMPLATE = 曜日ごと、NONE = 未設定（売上予算からの自動算出 AUTO は 9/23 に外した） */
  source: 'DAILY' | 'TEMPLATE' | 'NONE';
  required: number | null;
  assigned: number;
  breakdown: Array<BreakdownInfo>;
}

export interface BoardData {
  org: OrgInfo;
  setting: BoardSetting;
  role: RoleInfo;
  period: PeriodInfo;
  days: Array<DayHeader>;
  staffs: Array<StaffInfo>;
  shifts: Array<ShiftItem>;
  hopeShifts: Array<HopeShiftItem>;
  achievements: Array<AchievementItem>;
  violations: Array<ViolationItem>;
  summary: SummaryInfo;
  shiftPatterns: Array<PatternInfo>;
  businesses: Array<BusinessInfo>;
  supportOrgs: Array<OrgSimple>;
  requiredStaff: Array<RequiredStaffInfo>;
}

// ---------------------------------------------------------------------------------------------------------------- //
// 更新系

export type OperationType = 'CREATE' | 'UPDATE' | 'DELETE' | 'HOLIDAY' | 'MOVE';

export interface SupportRequestInput {
  deadline: string | null;
  note: string | null;
  requestApproval: boolean;
}

export interface Operation {
  op: OperationType;
  tempId?: string;
  shiftId?: string;
  expectedUpdateDate?: string | null;
  staffCode?: string;
  workOrgCode?: string;
  targetDate?: string;
  attendType?: number;
  isLegal?: boolean;
  shiftPatternId?: string | null;
  start?: string | null;
  end?: string | null;
  isEndNextDay?: boolean;
  breakMinutes?: number | null;
  businessId?: string | null;
  /** 元のシフト（前期間からコピー・カードの移動／コピー）。サーバは開始・終了が同じなら元の明細（休憩など）ごと写す（10/5） */
  sourceShiftId?: string | null;
  memo?: string;
  support?: SupportRequestInput | null;
  toStaffCode?: string;
  toTargetDate?: string;
  copy?: boolean;
}

export interface SaveRequest {
  orgCode: string;
  unlockAndReconfirm: boolean;
  dateFrom?: string;
  dateTo?: string;
  operations: Array<Operation>;
  /** 応答に差分（シフト・集計）を入れるか。画面は読み直すので false を送る（9/30） */
  includeDelta?: boolean;
}

export interface IdMapping {
  tempId: string;
  shiftId: string;
}

/** 保存したシフト 1 件（9/30）。画面は盤面を読み直さず、ID と保存後の更新日時を手元の盤面に当てる */
export interface SavedShift {
  /** 操作の番号（0 始まり） */
  index: number;
  tempId: string | null;
  shiftId: string;
  updateDate: string | null;
}

export interface Delta {
  affected: { staffCodes: Array<string>; dates: Array<string> };
  shifts: Array<ShiftItem>;
  removedShiftIds: Array<string>;
  idMapping: Array<IdMapping>;
  /** 保存したシフト（9/30 追加。古いサーバは返さない） */
  saved?: Array<SavedShift>;
  summary: SummaryInfo;
  days: Array<DayHeader>;
  reconfirmed: { dates: Array<string>; pendingDiffCount: number } | null;
}

export interface MoveRequest {
  orgCode: string;
  shiftId: string;
  expectedUpdateDate: string | null;
  dateFrom?: string;
  dateTo?: string;
  moveType: 'MOVE' | 'RESIZE_START' | 'RESIZE_END';
  start: string;
  end: string;
  isEndNextDay: boolean;
}

export interface ConfirmRequest {
  orgCode: string;
  dateFrom: string;
  dateTo: string;
  dryRun: boolean;
}

export interface NotifyTarget {
  staffCode: string;
  staffName: string;
  workDays: number;
  workMinutes: number;
  channel: string;
}

export interface DiffChange {
  date: string;
  before: string;
  after: string;
}

export interface DiffTarget {
  staffCode: string;
  staffName: string;
  changes: Array<DiffChange>;
}

export interface ConfirmPreview {
  targets: Array<NotifyTarget>;
  diffTargets: Array<DiffTarget>;
  laborCost: number | null;
  laborCostRate: number | null;
  violationCount: number;
  workMinutes: number;
  pendingSupportCount: number;
}

export interface ConfirmResult {
  days: Array<DayHeader>;
  period: PeriodInfo;
  pendingDiffTargets: number;
  /** 確定のあとにサーバで始めた人件費の再計算（10/5。始められなかったとき・古いサーバは無い） */
  recalculate?: RecalculateResult | null;
}

export interface Skipped {
  staffCode: string;
  targetDate: string;
  reason: string;
}

export interface GeneratedOperations {
  generated: number;
  operations: Array<Operation>;
  skipped: Array<Skipped>;
}

export interface AutoGenerateRequest {
  orgCode: string;
  dateFrom: string;
  dateTo: string;
  staffCodes: Array<string>;
  overwrite: boolean;
  respectHope: boolean;
}

export interface CopyRequest {
  orgCode: string;
  sourceFrom: string;
  sourceTo: string;
  targetFrom: string;
  staffCodes: Array<string>;
  /** 反映先に既にシフトがある日も対象にする（既存を置き換える操作を作る） */
  overwrite?: boolean;
}

// ---------------------------------------------------------------------------------------------------------------- //
// シミュレーション（T10）

/** セル内の 1 シフト（保存済みのコピー + 変更） */
export interface SimulationShift {
  tempId?: string;
  shiftId?: string;
  expectedUpdateDate?: string | null;
  attendType: number;
  isLegal?: boolean | null;
  shiftPatternId: string | null;
  shiftPatternShortName?: string | null;
  color?: string | null;
  start: string | null;
  end: string | null;
  isEndNextDay: boolean;
  breakMinutes: number | null;
  workMinutes?: number;
  businessId: string | null;
  /** 元のシフト（コピー・移動したもの）。保存のときに送る（10/5） */
  sourceShiftId?: string | null;
  workOrgCode: string;
  memo: string;
  support: SupportRequestInput | null;
  /** 削除予定（保存済みのものを消す） */
  deleted?: boolean;
}

export interface SimulationItem {
  staffCode: string;
  targetDate: string;
  shifts: Array<SimulationShift>;
  changed?: boolean;
}

// ---------------------------------------------------------------------------------------------------------------- //
// 予定人数・回収枠・提出状況・スタッフ詳細・支援候補

export interface RequiredStaffRow {
  dow: number | null;
  key: string | null;
  required: number;
}

export interface DailyRow {
  targetDate: string;
  key: string | null;
  required: number;
}

export interface RequiredStaffData {
  breakdownAxis: BreakdownAxis;
  template: Array<RequiredStaffRow>;
  daily: Array<DailyRow>;
}

export interface PeriodData {
  periodId: string;
  orgCode: string;
  name: string;
  dateFrom: string;
  dateTo: string;
  deadline: string;
  format: string;
  status: 'collecting' | 'closed';
  confirmedAt: string | null;
  submittedCount: number;
  targetCount: number;
}

export interface PeriodCreateRequest {
  orgCode: string;
  name: string;
  dateFrom: string;
  dateTo: string;
  deadline: string;
  format: 'pattern' | 'time' | 'symbol';
  notify: boolean;
}

export interface SubmissionRow {
  staffCode: string;
  staffName: string;
  employmentName: string | null;
  submitted: boolean;
  submittedAt: string | null;
  tendencyWorkDays: number | null;
  tendencyMainPattern: string | null;
  comment: string | null;
  remindCount: number;
  hasMail: boolean;
}

export interface SubmissionsData {
  period: PeriodData | null;
  countdown: { daysLeft: number; overdue: boolean };
  stats: { submitted: number; unsubmitted: number; rate: number };
  rows: Array<SubmissionRow>;
  /** 提出状況を編集してよいか（9/17 決定） */
  editable: boolean;
}

export interface WeekPattern {
  dow: number;
  shiftPatternId: string | null;
  shiftPatternName: string | null;
  holidayId: string | null;
  isLegal: boolean | null;
}

export interface StaffDetail {
  staff: StaffInfo;
  weekPatterns: Array<WeekPattern>;
  hopeShifts: Array<HopeShiftItem>;
}

export interface SupportCandidate {
  staffCode: string;
  staffName: string;
  belongOrgName: string;
  /** 役職名。雇用形態名は返さない */
  executiveName: string | null;
  hasShiftOnDate: boolean;
}

/** 2-2 共通エラーレスポンス */
export interface ApiErrorBody {
  message?: string;
  errors?: Array<{ index: number | null; field: string | null; message: string }>;
  current?: unknown;
  lockReason?: LockReason;
  dates?: Array<string>;
}
